// Faz BP-polish (2026-05-13) — Login redesign
// Eski Login.js sade bir kart + gri arka plandı; brand/theme tokenlerini hiç
// kullanmıyordu (hardcoded #1a237e, gri bg). Bu sürüm:
//   - Sidebar koyu mavi → primary mavi gradient arka plan (marka tutarli)
//   - MK monogram inline SVG logo (logo.png yerine — kesinlik)
//   - "Şifremi unuttum" linki → /forgot-password
//   - Mobil için fullWidth+max-width-aware kart
//   - Theme renkleri (sidebarTokens + lightTokens) tokenlerden geliyor
//   - Inputlar size="medium", autofocus email
//   - Footer: minimal copyright

import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Card, TextField, Button, Typography, Alert, Stack, Link } from '@mui/material';
import { login } from '../services/api';

const Logo = ({ size = 64 }) => (
  // Inline SVG — fontsiz, retina-keskin, light/dark tema-bagimsiz
  <Box
    aria-label="Miksan Plastik"
    sx={{
      width: size, height: size, borderRadius: '50%',
      bgcolor: '#FFFFFF',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 4px 18px rgba(15,42,79,0.30)',
      mx: 'auto'
    }}
  >
    <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <text x="50" y="55" textAnchor="middle" dominantBaseline="middle"
            fontFamily="Inter, system-ui, sans-serif" fontWeight="800" fontSize="42" fill="#1E4FA0">
        MK
      </text>
    </svg>
  </Box>
);

export default function Login() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await login(form);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      navigate('/dashboard');
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401) setError('E-posta veya şifre hatalı');
      else if (code === 403) setError('Bu hesap admin erişimi olmayan bir hesap');
      else if (code === 429) setError('Çok fazla deneme. Lütfen birkaç dakika bekleyin.');
      else setError('Giriş başarısız — bağlantı veya sunucu hatası');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // sidebarBg (#0F2A4F) → primary (#1E4FA0) diagonal gradient
        background: 'linear-gradient(135deg, #0F2A4F 0%, #1E4FA0 100%)',
        px: 2,
        py: 4,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(15,42,79,0.18), 0 2px 8px rgba(15,42,79,0.10)',
        }}
        elevation={0}
      >
        <Stack spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Logo size={72} />
          <Box textAlign="center">
            <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary', mb: 0.5 }}>
              Miksan Plastik
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Yönetim Paneli
            </Typography>
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="E-posta"
            type="email"
            autoComplete="username"
            autoFocus
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Şifre"
            type="password"
            autoComplete="current-password"
            value={form.password}
            onChange={e => setForm({ ...form, password: e.target.value })}
            sx={{ mb: 1 }}
            required
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              underline="hover"
              sx={{ fontSize: 13, color: 'primary.main', fontWeight: 500 }}
            >
              Şifremi unuttum
            </Link>
          </Box>

          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            size="large"
            sx={{
              py: 1.4,
              fontWeight: 700,
              textTransform: 'none',
              fontSize: 15,
              borderRadius: 2,
            }}
          >
            {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
          </Button>
        </form>

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.disabled', mt: 3, fontSize: 11 }}>
          © {new Date().getFullYear()} Miksan Plastik · Tüm hakları saklıdır
        </Typography>
      </Card>
    </Box>
  );
}
